import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { AxiosError } from "axios";
import {
  updateWorkshopInstance,
  deleteWorkshopInstance,
  getWorkshopInstanceDetails,
  addWorkshopParticipant,
  removeWorkshopParticipant,
  closeAdmission,
  completeAttendance,
  closeFeedback,
  reopenFeedback,
  getFeedbackQuestions,
  submitWorkshopFeedback as submitWorkshopFeedbackApi,
  getWorkshopInstanceFeedback,
} from "../../api/workshops";
import {
  UpdateWorkshopInstanceDto,
  WorkshopInstanceUserFeedbackDto,
  WorkshopParticipantDto,
} from "../../types/workshopTypes";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { MembersFieldDto } from "../../types/membersTypes";

/**
 * Hook that handles the workshop instances api calls, uses react-query
 * @returns Functions to create, update, and delete workshop instances
 */
const useWorkshopInstanceDetails = (workshopId: number, workshopInstanceId: number) => {
  const { auth, dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ----------------------------------------------------------------------------------
  // ############
  // GET QUERIES
  // ############

  // workshopInstanceDetails query
  const {
    data: workshopInstanceData,
    isLoading: isWorkshopInstanceDetailsLoading,
    isError: isWorkshopInstanceDetailsError,
  } = useQuery({
    queryKey: ["workshopInstanceDetails", workshopInstanceId],
    queryFn: () => getWorkshopInstanceDetails(workshopId, workshopInstanceId),
    enabled: !!workshopInstanceId,
  });

  const workshopInstanceDetails = workshopInstanceData?.data;

  // Get feedback questions query
  const { data: workshopInstanceFeedbackQuestionsData } = useQuery({
    queryKey: ["workshopInstanceFeedbackQuestions", workshopInstanceId],
    queryFn: () => getFeedbackQuestions(workshopId, workshopInstanceId),
    enabled: !!workshopInstanceId,
  });

  const workshopInstanceFeedbackQuestions = workshopInstanceFeedbackQuestionsData?.data;

  // Get feedback for a workshop instance query
  const { data: workshopInstanceFeedbackData } = useQuery({
    queryKey: ["workshopInstanceFeedback", workshopInstanceId],
    queryFn: () => getWorkshopInstanceFeedback(workshopId, workshopInstanceId),
    enabled: !!workshopInstanceId,
  });

  const workshopInstanceFeedback = workshopInstanceFeedbackData?.data;

  // ----------------------------------------------------------------------------------
  // ############
  // POST MUTATIONS
  // ############

  // addWorkshopParticipant mutation
  const { mutateAsync: mutateAddWorkshopParticipant } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      member,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      member: MembersFieldDto;
    }) => addWorkshopParticipant(workshopId, workshopInstanceId, member.memberId),
    onError: (err: AxiosError) => {
      showErrorMessage("Teilnehmer hinzufügen fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { member, workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      if (auth.userID === member.memberId) {
        showSuccessMessage("Du wurdest angemeldet");
      } else {
        showSuccessMessage("Teilnehmer erfolgreich hinzugefügt");
      }
    },
  });

  /**
   * Function to add a participant to a workshop instance
   * @param member The member to add
   */
  const addWorkshopParticipantHandler = async (member: MembersFieldDto) => {
    return await mutateAddWorkshopParticipant({ workshopId, workshopInstanceId, member });
  };

  // submitFeedback mutation
  const { mutateAsync: mutateSubmitFeedback } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      feedbackData,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      feedbackData: WorkshopInstanceUserFeedbackDto;
    }) => submitWorkshopFeedbackApi(workshopId, workshopInstanceId, feedbackData),
    onError: (err: AxiosError) => {
      showErrorMessage("Feedback absenden fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceFeedback", workshopInstanceId] });
      await queryClient.invalidateQueries({ queryKey: ["pendingFeedbackWorkshopInstances"] });
      showSuccessMessage("Feedback erfolgreich abgesendet");
      navigate(`/workshops/${workshopId}`);
    },
  });

  /**
   * Function to submit feedback for a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance
   * @param feedbackData The feedback data to submit
   * @return Promise that resolves when the feedback is submitted
   */
  const submitWorkshopFeedback = async (feedbackData: WorkshopInstanceUserFeedbackDto) => {
    return await mutateSubmitFeedback({ workshopId, workshopInstanceId, feedbackData });
  };

  // ----------------------------------------------------------------------------------
  // ############
  // UPDATE MUTATIONS
  // ############

  // updateWorkshopInstance mutation
  const { mutateAsync: mutateUpdateWorkshopInstance } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      workshopInstanceData,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      workshopInstanceData: UpdateWorkshopInstanceDto;
    }) => updateWorkshopInstance(workshopId, workshopInstanceId, workshopInstanceData),
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop-Instanz aktualisieren fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      showSuccessMessage("Workshop-Instanz erfolgreich aktualisiert");
    },
  });

  /**
   * Function to update an existing workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance to update
   * @param workshopInstanceData The workshop instance data to update
   */
  const updateWorkshopInstanceHandler = async (
    workshopId: number,
    workshopInstanceId: number,
    workshopInstanceData: UpdateWorkshopInstanceDto
  ) => {
    return await mutateUpdateWorkshopInstance({ workshopId, workshopInstanceId, workshopInstanceData });
  };

  // closeAdmission mutation
  const { mutateAsync: mutateCloseAdmission } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      workshopParticipantsData,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      workshopParticipantsData: WorkshopParticipantDto[];
    }) => closeAdmission(workshopId, workshopInstanceId, workshopParticipantsData),
    onError: (err: AxiosError) => {
      if (err.response?.status === 409) {
        showErrorMessage("Anmeldung kann nicht geschlossen werden");
      } else {
        showErrorMessage("Anmeldung schließen fehlgeschlagen");
      }
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      showSuccessMessage("Anmeldung erfolgreich geschlossen");
    },
  });

  /**
   * Function to close the admission of a workshop instance
   * @param workshopParticipantsData The workshop participants data to close the admission for
   */
  const closeAdmissionHandler = async (workshopParticipantsData: WorkshopParticipantDto[]) => {
    return await mutateCloseAdmission({ workshopId, workshopInstanceId, workshopParticipantsData });
  };

  // completeAttendance mutation
  const { mutateAsync: mutateCompleteAttendance } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      workshopParticipantsData,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      workshopParticipantsData: WorkshopParticipantDto[];
    }) => completeAttendance(workshopId, workshopInstanceId, workshopParticipantsData),
    onError: (err: AxiosError) => {
      if (err.response?.status === 409) {
        showErrorMessage("Anwesenheit kann nicht erfasst werden");
      } else {
        showErrorMessage("Anwesenheit erfassen fehlgeschlagen");
      }
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      showSuccessMessage("Anwesenheit erfolgreich erfasst");
    },
  });

  /**
   * Function to complete the attendance of a workshop instance
   * @param workshopParticipantsData The workshop participants data to complete the attendance for
   */
  const completeAttendanceHandler = async (workshopParticipantsData: WorkshopParticipantDto[]) => {
    return await mutateCompleteAttendance({ workshopId, workshopInstanceId, workshopParticipantsData });
  };

  // closeFeedback mutation
  const { mutateAsync: mutateCloseFeedback, isPending: isClosingFeedback } = useMutation({
    mutationFn: ({ workshopId, workshopInstanceId }: { workshopId: number; workshopInstanceId: number }) =>
      closeFeedback(workshopId, workshopInstanceId),
    onError: (err: AxiosError) => {
      showErrorMessage("Feedback kann nicht geschlossen werden");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceFeedback", workshopInstanceId] });
      showSuccessMessage("Feedback erfolgreich geschlossen");
    },
  });

  /**
   * Function to close the feedback of a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance to close the feedback of
   */
  const closeFeedbackHandler = async () => {
    return await mutateCloseFeedback({ workshopId, workshopInstanceId });
  };

  // reopenFeedback mutation

  const { mutateAsync: mutateReopenFeedback, isPending: isReopeningFeedback } = useMutation({
    mutationFn: ({ workshopId, workshopInstanceId }: { workshopId: number; workshopInstanceId: number }) =>
      reopenFeedback(workshopId, workshopInstanceId),
    onError: (err: AxiosError) => {
      showErrorMessage("Feedback kann nicht wieder geöffnet werden");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceFeedback", workshopInstanceId] });
      showSuccessMessage("Feedback erfolgreich wieder geöffnet");
    },
  });

  /**
   * Function to reopen the feedback of a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance to reopen the feedback of
   */
  const reopenFeedbackHandler = async () => {
    return await mutateReopenFeedback({ workshopId, workshopInstanceId });
  };

  // ----------------------------------------------------------------------------------
  // ############
  // DELETE QUERIES
  // ############

  // deleteWorkshopInstance mutation
  const { mutateAsync: mutateDeleteWorkshopInstance } = useMutation({
    mutationFn: ({ workshopId, workshopInstanceId }: { workshopId: number; workshopInstanceId: number }) =>
      deleteWorkshopInstance(workshopId, workshopInstanceId),
    onError: (err: AxiosError) => {
      if (err.response?.status === 409) {
        showErrorMessage("Workshop-Termin kann nicht gelöscht werden");
      } else {
        showErrorMessage("Workshop-Termin löschen fehlgeschlagen");
      }
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopId] });
      showSuccessMessage("Workshop-Termin erfolgreich gelöscht");
      // Redirect to workshop details after successful deletion
      navigate(`/workshops/${workshopId}`);
    },
  });

  /**
   * Function to delete a workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceId The ID of the workshop instance to delete
   */
  const deleteWorkshopInstanceHandler = async (workshopId: number, workshopInstanceId: number) => {
    return await mutateDeleteWorkshopInstance({ workshopId, workshopInstanceId });
  };

  // removeWorkshopParticipant mutation
  const { mutateAsync: mutateRemoveWorkshopParticipant } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceId,
      member,
    }: {
      workshopId: number;
      workshopInstanceId: number;
      member: MembersFieldDto;
    }) => removeWorkshopParticipant(workshopId, workshopInstanceId, member.memberId),
    onError: (err: AxiosError) => {
      showErrorMessage("Teilnehmer entfernen fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { member, workshopInstanceId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstanceDetails", workshopInstanceId] });
      if (auth.userID === member.memberId) {
        showSuccessMessage("Du wurdest abgemeldet");
      } else {
        showSuccessMessage("Teilnehmer erfolgreich entfernt");
      }
    },
  });

  /**
   * Function to remove a participant from a workshop instance
   * @param member The member to remove
   */
  const removeWorkshopParticipantHandler = async (member: MembersFieldDto) => {
    return await mutateRemoveWorkshopParticipant({ workshopId, workshopInstanceId, member });
  };

  return {
    updateWorkshopInstance: updateWorkshopInstanceHandler,
    deleteWorkshopInstance: deleteWorkshopInstanceHandler,
    addWorkshopParticipant: addWorkshopParticipantHandler,
    removeWorkshopParticipant: removeWorkshopParticipantHandler,
    closeAdmission: closeAdmissionHandler,
    completeAttendance: completeAttendanceHandler,
    closeFeedback: closeFeedbackHandler,
    reopenFeedback: reopenFeedbackHandler,
    submitWorkshopFeedback: submitWorkshopFeedback,
    isReopeningFeedback,
    isClosingFeedback,
    // Data
    workshopInstanceDetails,
    isWorkshopInstanceDetailsLoading,
    isWorkshopInstanceDetailsError,
    workshopInstanceFeedbackQuestions,
    workshopInstanceFeedback,
  };
};

export default useWorkshopInstanceDetails;
